import {
  Box,
  HStack,
  Text,
  Heading,
  Spacer,
  Flex,
  useColorModeValue,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { DataPipeline } from '@finley-technologies/finley-types';

import { colors } from 'packages/common-frontend/colors';
import { IconButton } from 'packages/common-frontend/components/IconButton';
import { Icon as FaIcon } from 'packages/common-frontend/components/icons';
import { useSlugToName } from 'packages/dataPipeline/hooks/slugToNameContext';
import { getStatusIcon } from 'packages/dataPipeline/ui/list/PipelineStatusIndicator';
import { getReportingDateVersionString } from 'packages/dataPipeline/ui/utils/getReportingDateVersionString';
import { getRelativeTime } from 'packages/dataPipeline/ui/utils/getTimeElapsed';

export const MeshCard = ({
  meshRow,
}: {
  meshRow: DataPipeline.MeshRunRecord;
}) => {
  const location = useLocation();
  const { cfSlugToName } = useSlugToName();
  const searchParams = location.search;

  const reportingDateVersionStr = getReportingDateVersionString(
    meshRow.reportingDate,
    meshRow.fileVersion,
  );

  const startedAtStr = new Date(meshRow.startedAt).toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZoneName: 'short',
  });

  const subTextColor = useColorModeValue(colors.black[70], colors.gray[300]);
  const statusIcon = getStatusIcon(meshRow.status);
  const bgColor = useColorModeValue(colors.background[50], colors.gray[800]);
  const borderColor = useColorModeValue(colors.gray[200], colors.gray[600]);

  return (
    <Box borderWidth="1px" borderRadius="lg" p={0}>
      <Box
        bg={bgColor}
        p="16px"
        w="100%"
        borderBottom="1px"
        borderColor={borderColor}
      >
        <Heading size="16px" fontWeight={500}>
          {meshRow.cfSlugs.map(cfSlugToName).join(', ')}
        </Heading>
      </Box>
      <Box p="16px">
        <HStack spacing="24px" alignItems="flex-start">
          <Box w="100px" h="60px" textAlign="center">
            <Tooltip label={statusIcon.label} placement="top">
              <Box display="inline-block">
                <FaIcon
                  icon={statusIcon.icon}
                  color={statusIcon.textColor}
                  size="24px"
                />
              </Box>
            </Tooltip>
            <Tooltip label="Last Updated" placement="bottom">
              <Text
                mt="8px"
                fontSize="14px"
                fontWeight={400}
                color={subTextColor}
              >
                {getRelativeTime(meshRow.updatedAt)}
              </Text>
            </Tooltip>
          </Box>
          <Box>
            <Link
              to={`./details/${meshRow.meshRunId}${searchParams}`}
              style={{
                color: colors.blue[400],
                fontSize: '16px',
                textDecoration: 'underline',
                wordBreak: 'break-all',
              }}
            >
              {meshRow.meshRunId}
            </Link>
            <Flex mt="8px" width="100%" alignItems="center">
              <FaIcon icon="calendar" color={subTextColor} />
              <Text fontSize="14px" color={subTextColor} ml="8px">
                {reportingDateVersionStr}
              </Text>
            </Flex>
          </Box>
          <Spacer />
          <Box>
            <Text fontSize="14px" fontWeight={400} color={subTextColor} mt="0">
              Started {startedAtStr}
            </Text>
          </Box>
        </HStack>
      </Box>
    </Box>
  );
};

export const MeshMiniCard = ({
  meshRow,
  onClick,
}: {
  meshRow: DataPipeline.MeshRunRecord;
  onClick?: () => void;
}) => {
  const subTextColor = useColorModeValue(colors.gray[700], colors.gray[300]);
  const reportingDateVersionStr = getReportingDateVersionString(
    meshRow.reportingDate,
    meshRow.fileVersion,
  );

  const startedAtStr = new Date(meshRow.startedAt).toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZoneName: 'short',
  });

  const statusIcon = getStatusIcon(meshRow.status);

  return (
    <Box p="14px" borderWidth="1px" borderRadius="lg" width="100%">
      <HStack spacing="24px" alignItems="flex-start">
        <VStack alignItems="flex-start">
          <Text>{reportingDateVersionStr}</Text>
          <Text fontSize="14px" color={subTextColor}>
            Started {startedAtStr}
          </Text>
        </VStack>
        <Spacer />
        <HStack>
          <FaIcon
            icon={statusIcon.icon}
            color={statusIcon.textColor}
            size="24px"
          />
          <Text>{statusIcon.label}</Text>
          <IconButton
            ariaLabel="Open"
            label="Open"
            icon={<FaIcon icon="angle-right" size="16px" />}
            disabled={!onClick}
            onClick={onClick}
          />
        </HStack>
      </HStack>
    </Box>
  );
};
